import React from 'react';
import { NumberUtils, Misc, Model } from '@singularsystems/neo-core';
import { Neo, NeoGrid } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import { BalancesTrancheSummary } from './BalancesTrancheSummary';
import { PortfolioHelpers } from '../../Components/PortfolioHelpers';
import IncentiveGroup, { VestingType } from '../../../Models/Portfolio/IncentiveGroup';
import CalculationGroup from '../../../Models/Portfolio/Calculation/CalculationGroup';

interface IBalancesGroupedGridProps {
    category: IncentiveGroup;
    vestingType: VestingType;
    editMode: boolean;
    hasCostsAlreadyPaidGroups : boolean;
    applyPayoutRatio: boolean;
}

@observer
export class BalancesGroupedGrid extends React.Component<IBalancesGroupedGridProps> {

    public render() {
        const category = this.props.category,
            groupsWithBalance = category.groupedList.filter(c => c.hasPortfolioBalance),
            currencySymbols = groupsWithBalance.groupBy(c => c.instrument.displayCurrencySymbol, key => key),
            canSum = currencySymbols.length === 1,
            showInstrumentColumn = this.showInstrumentColumn(),
            showEntitiesColumn = this.showEntitiesColumn(),
            hasMultipleInstruments = category.hasMultipleInstruments(),
            hasCostsAlreadyPaidGroups = this.props.hasCostsAlreadyPaidGroups

        return (
            <NeoGrid.Grid items={groupsWithBalance} initialSort="awardDate" className="compact-editors portfolio-balance-grid">
                {(item, meta) => (
                    <>
                        {this.renderItem(
                            item, 
                            meta, 
                            canSum, 
                            this.props.category.info.isCash, 
                            showInstrumentColumn, 
                            showEntitiesColumn, 
                            hasMultipleInstruments, 
                            hasCostsAlreadyPaidGroups,
                            category.showUnitColumns,
                            category.showPriceColumns,
                            category.showAwardCostsColumn,
                            this.props.applyPayoutRatio)}
                    </>
                )}
            </NeoGrid.Grid>
        )
    }

    private showInstrumentColumn() {
        for (var item of this.props.category.groupedList) {
            if (item.trancheBalance.incentiveScheme.instruments.length > 1) {
                return true;
            }
        }
        return false;
    }

    private showEntitiesColumn() {
        return this.props.category.groupedList.some(x => x.trancheBalance.hasParticipantEntity);
    }

    private onSchemeHover(item: CalculationGroup, isHovered: boolean) {
        if (item && item.groupLink) {
            item.groupLink.isHovered = isHovered;
        }
    }

    private onSelectedClick(e: React.MouseEvent, item: CalculationGroup) {
        const groups = this.props.category.groupedList.filter(c => c.vestedBalance > 0),
            selectAll = groups.some(c => !c.isSelected);

        groups.forEach(g => g.isSelected = selectAll);
    }

    private formatString(currencySymbol: string) {
        return `${currencySymbol} #,##0.00######;(${currencySymbol}#,##0.00######)`;
    }

    private renderItem(
        item: CalculationGroup, 
        meta: Model.TransformMetaType<CalculationGroup>, 
        canSum: boolean, 
        isCashIncentiveGroup: boolean, 
        showInstrumentColumn: boolean, 
        showEntitiesColumn: boolean, 
        hasMultipleInstruments: boolean, 
        hasCostsAlreadyPaidGroups: boolean,
        showUnitColumns: boolean,
        showPriceColumns: boolean,
        showAwardCostsColumn: boolean,
        applyPayoutRatio: boolean,) 
    {
        const vestingType = this.props.vestingType,
            editMode = this.props.editMode;

        const metricLabel = hasCostsAlreadyPaidGroups? "value" : "profit";
        const metricLabelHeadingTooltip = hasCostsAlreadyPaidGroups? "Current market value of all shares and options." : "Total potential profit (gross value - award costs) at the current market price for all units (vested and unvested).";
        const metricLabelColumnTooltip = hasCostsAlreadyPaidGroups? "Current market value of vested shares and options." : "Current profit (gross value - award costs) of vested units";

        const totalProfitHeading = vestingType === VestingType.UnvestedOnly ? `Unvested potential\n${metricLabel}` : `Total potential\n${metricLabel}`,
            unitsHeading = vestingType === VestingType.AllAwards ? "Total\nunits" : vestingType === VestingType.VestedOnly ? "Vested\nunits" : "Unvested\nunits";

        return (<NeoGrid.RowGroup expandProperty={meta.isExpanded}>
            <NeoGrid.Row>
                {vestingType !== VestingType.UnvestedOnly &&
                    <NeoGrid.Column
                        label={"Select all"}
                        display={meta.isSelected}
                        input={{ type: "checkbox" }}
                        headerClassName="select-all"
                        cellClassName={"award-select" + (item.vestedBalance > 0 ? " icon-container-client-secondary" : "")}
                        sort={false}
                        onHeaderClick={e => this.onSelectedClick(e, item)}
                        onClick={() => item.isSelected = !item.isSelected}
                        data-tip={item.vestedBalance === 0 ? "This award has no vested units" : ""}
                        data-tip-pos="right" />}

                {showEntitiesColumn &&
                    <NeoGrid.Column label="Entity Name" display={meta.participantEntityName} />}

                <NeoGrid.Column
                    label="Scheme"
                    bindContext={meta.incentiveSchemeName}
                    className="text-heading"
                    headerClassName="bold"
                    cellClassName={item?.groupLink?.isHovered ? "bold" : ""}
                    onMouseEnter={() => this.onSchemeHover(item, true)} onMouseLeave={() => this.onSchemeHover(item, false)}
                    footerContent={() => ""}>
                    <span className={item.groupLink?.isHovered ? "bold" : ""}>
                        {item.groupLink && <i className="fa fa-link me-2" data-tip={"Linked award: " + item.groupLink.awardLinkDescription} />}
                        {item.details?.some(detail =>
                             detail.trancheBalance.hasUnprocessedAwardCondition &&
                             detail.trancheBalance.payoutRatio != null) &&
                            <i className="fa fa-arrow-trend-up me-2" data-tip="Payout ratio % is enabled for this award"/>
                        }
                        {item.incentiveSchemeName}
                    </span>
                    {item.trancheBalance.hasUpcomingExpiry && <span
                        className="badge text-bg-warning"
                        style={{ marginLeft: 5 }}
                        data-tip-class="grid-tooltip"
                        data-tip={"This award has an upcoming expiry."}>
                        Expiring Soon
                    </span>}
                </NeoGrid.Column>

                {showInstrumentColumn &&
                    <NeoGrid.Column
                        label="Instrument"
                        display={meta.instrumentName}
                        headerClassName="bold"
                    />}

                <NeoGrid.Column display={meta.awardDate} hideBelow="xl" />

                {showUnitColumns &&
                    <NeoGrid.Column 
                        label={unitsHeading} 
                        display={item.showUnitColumns ? (applyPayoutRatio && item.payoutUnits !== item.availableBalance ? meta.payoutUnits : meta.availableBalance) : meta.emptyPlaceholder} 
                        numProps={{ formatString: "#,##0.##" }} 
                        className="bold" 
                        cellClassName={(applyPayoutRatio && (item.payoutUnits !== item.availableBalance || item.details.some(d => d.trancheBalance.hasUnprocessedAwardCondition))) ? "payout-units" : undefined}                        cellTooltip={applyPayoutRatio && item.showUnitColumns && item.payoutUnits !== item.availableBalance
                            ? `Payout ratio units: ${NumberUtils.format(item.payoutUnits, "#,##0.##")} / Total units: ${NumberUtils.format(item.availableBalance, "#,##0.##")} (${NumberUtils.format(item.payoutUnits / item.availableBalance * 100, "#,##0.##")}%)`
                            : undefined}
                        footerContent={(data: CalculationGroup[]) => !hasMultipleInstruments ?
                            NumberUtils.format(data.filter(c => c.showUnitColumns).sum(c => applyPayoutRatio && c.payoutUnits !== c.availableBalance ? c.payoutUnits : c.availableBalance), "#,##0.##") : null} />}

                {vestingType === VestingType.AllAwards && showUnitColumns &&
                    <NeoGrid.Column label={"Vested\nunits"} display={item.showUnitColumns ? meta.vestedBalance : meta.emptyPlaceholder} numProps={{ formatString: "#,##0.##" }} hideBelow="xl" />}

                {showPriceColumns && 
                    <NeoGrid.Column label={"Award\nprice"} display={item.showPriceColumns ? meta.awardPrice : meta.emptyPlaceholder} numProps={{ zeroText: "-", formatString: this.formatString(item.priceCurrencySymbol) }} hideBelow="md" />
                }

                {showPriceColumns && 
                    <NeoGrid.Column label={"Current\nprice"} cellClassName="stacked-column" alignment="right" headerClassName="bold" style={{ width: editMode ? 120 : "auto" }}>
                        {() => editMode && item.trancheBalance.fixedPrice === null ?
                            <Neo.NumberInput 
                            bind={meta.customPrice} 
                            numProps={{ currencySymbol: item.priceCurrencySymbol }} /> :
                            PortfolioHelpers.getProfitLoss(item.trancheBalance.pricePercent, (value, icon) =>
                                <>
                                    {item.trancheBalance.fixedPrice !== null && item.showPriceColumns &&
                                        <i className="fa fa-tags me-2" data-tip={"Price is fixed at " + NumberUtils.format(item.trancheBalance.fixedPrice, Misc.NumberFormat.CurrencyDecimals, item.priceCurrencySymbol)} />}
                                    <div className="price-details">
                                        <strong>{item.showPriceColumns ? NumberUtils.format(item.instrumentPrice, this.formatString(item.priceCurrencySymbol)) : item.emptyPlaceholder}</strong>
                                        {item.trancheBalance.awardPriceForGain > 0 && item.showPriceColumns && <div>{icon} <span className="small">{NumberUtils.format(item.trancheBalance.pricePercent, Misc.NumberFormat.PercentDecimals)}</span></div>}
                                    </div>
                                </>)
                        }
                    </NeoGrid.Column>
                }

                <NeoGrid.Column
                    label={"Current gross\nvalue"}
                    display={applyPayoutRatio && item.payoutCurrentValueConverted !== item.currentValueConverted ? meta.payoutCurrentValueConverted : meta.currentValueConverted}
                    numProps={{ currencySymbol: item.instrument.displayCurrencySymbol }}
                    footerContent={(data: CalculationGroup[]) => canSum ?
                        NumberUtils.format(data.sum(c => applyPayoutRatio && c.payoutCurrentValueConverted !== c.currentValueConverted ? c.payoutCurrentValueConverted : c.currentValueConverted), Misc.NumberFormat.CurrencyDecimals, item.instrument.displayCurrencySymbol) : null}
                    hideBelow="sm"
                    headerTooltip="Number of units x current share price (before any costs)" />

                {showAwardCostsColumn && 
                    <NeoGrid.Column 
                    label={"Award\ncosts/income"} 
                    display={item.showAwardCostsColumn ? (applyPayoutRatio && item.payoutRemainingAwardDebtConverted !== item.remainingAwardDebtConverted ? meta.payoutRemainingAwardDebtConverted : meta.remainingAwardDebtConverted) : meta.emptyPlaceholder} 
                    numProps={{ currencySymbol: item.instrument.displayCurrencySymbol }} 
                    footerContent={(data: CalculationGroup[]) => canSum ?
                            NumberUtils.format(data.filter(c => c.showAwardCostsColumn).sum(c => applyPayoutRatio && c.payoutRemainingAwardDebtConverted !== c.remainingAwardDebtConverted ? c.payoutRemainingAwardDebtConverted : c.remainingAwardDebtConverted), Misc.NumberFormat.CurrencyDecimals, item.instrument.displayCurrencySymbol) : null}
                    hideBelow="md" />
                }  

                {vestingType !== VestingType.UnvestedOnly &&
                    <NeoGrid.Column
                        label={`Current vested\npotential ${metricLabel}`}
                        display={applyPayoutRatio ? meta.payoutVestedProfitLossConvertedLimited : meta.vestedProfitLossConvertedLimited}
                        numProps={{ currencySymbol: item.instrument.displayCurrencySymbol }}
                        cellClassName={"cell-highlight-1 " + ((applyPayoutRatio ? item.payoutVestedProfitLossConvertedLimited : item.vestedProfitLossConvertedLimited) < 0 ? "grid-loss" : "grid-highlight") + (item.isExpanded || vestingType === VestingType.VestedOnly ? " bold" : " ")}
                        headerClassName="bold grid-highlight"
                        footerContent={(data: CalculationGroup[]) => canSum ?
                            NumberUtils.format(data.sum(c => c.limitLossToZero(c => applyPayoutRatio ? c.payoutVestedProfitLossConverted : c.vestedProfitLossConverted, true)), Misc.NumberFormat.CurrencyDecimals, item.instrument.displayCurrencySymbol) : null}
                        hideBelow="lg"
                        headerTooltip={metricLabelColumnTooltip} />}

                {vestingType !== VestingType.VestedOnly &&
                    <NeoGrid.Column
                        label={totalProfitHeading}
                        display={applyPayoutRatio ? meta.payoutProfitLossConvertedLimited : meta.profitLossConvertedLimited}
                        numProps={{ currencySymbol: item.instrument.displayCurrencySymbol }}
                        cellClassName={"cell-highlight-2" + ((applyPayoutRatio ? item.payoutProfitLossConvertedLimited : item.profitLossConvertedLimited) < 0 ? " grid-loss" : "") + (item.isExpanded ? "" : " bold")}
                        headerClassName="bold"
                        footerContent={(data: CalculationGroup[]) => canSum ?
                            NumberUtils.format(data.sum(c => c.limitLossToZero(c => applyPayoutRatio ? c.payoutProfitLossConverted : c.profitLossConverted, true)), Misc.NumberFormat.CurrencyDecimals, item.instrument.displayCurrencySymbol) : null}
                        headerTooltip={metricLabelHeadingTooltip} />}
            </NeoGrid.Row>
            <NeoGrid.ChildRow isFullWidth childCellProps={{ className: "tranche-child-row", colSpan: 12}} >
                <BalancesTrancheSummary 
                    group={item} 
                    hasCostsAlreadyPaidGroups={hasCostsAlreadyPaidGroups}
                    applyPayoutRatio={applyPayoutRatio}
                    />
            </NeoGrid.ChildRow>
        </NeoGrid.RowGroup>)
    }
}